import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Form, Input, Button, Space, Typography } from 'antd';
import { toast } from 'react-hot-toast';
import updateNoteTemplate from '../../utils/note_templates/updateNoteTemplate';
import createNoteTemplate from '../../utils/note_templates/createNoteTemplate';
import Markdown from '../Markdown';

const { TextArea } = Input;
const { Text } = Typography;

const NoteTemplateUpdateModal = ({ 
  open, 
  onCancel, 
  onSave,
  template
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const creatingNewTemplate = template?.getUUID() === 'new-template';

  useEffect(() => {
    if (open && template) {
      form.setFieldsValue({
        name: template.getName(),
        content: template.getContent()
      });
      // Open in edit mode for new templates or when requested by caller
      if (creatingNewTemplate) {
        setIsEditing(true);
      }
    }
  }, [open, template, form]);

  const handleSave = async (values) => {
    try {
      setIsSaving(true);
      let result;
      
      if (template.getUUID() === 'new-template') {
        // Creating a new template
        result = await createNoteTemplate(
          values.name,
          values.content,
          null // No file for text-based creation
        );
        if (result) {
          toast.success(t('note_templates.create_success'));
        }
      } else {
        // Updating existing template
        result = await updateNoteTemplate(
          template.getUUID(),
          values.name,
          values.content
        );
        if (result) {
          toast.success(t('note_templates.update_success'));
        }
      }
      
      if (result) {
        onSave(result);
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(t('note_templates.errors.save'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setIsEditing(false);
    onCancel();
  };

  if (!template) return null;

  return (
    <Modal
      title={creatingNewTemplate ? t('note_templates.create_title') : t('note_templates.edit_title')}
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={800}
      maskClosable={false}
    >

      {creatingNewTemplate && 
        <div style={{ fontSize: '13px', alignItems: 'center', display: 'flex', marginBottom: 20, flexDirection: 'column' }}>
          <Text>{t('note_templates.create_subtitle_3')}</Text>
        </div>
      }
      <Form form={form} onFinish={handleSave} layout="vertical">
        <Form.Item
          name="content"
          label={<Text type="secondary">{t('note_templates.content')}</Text>}
          extra={
          <div style={{ marginTop: 8, textAlign: 'right' }}>
            <Text type="secondary" style={{ fontSize: '13px', display: 'inline-block', maxWidth: 475 }}>
              {t('note_templates.content_markdown_hint')}
            </Text>
          </div>
          }
        >
          {isEditing ? (
            <TextArea 
              rows={15} 
              placeholder={t('note_templates.content_placeholder')}
              onBlur={() => setIsEditing(false)}
              autoFocus
              style={{ minHeight: '400px' }}
            />
          ) : (
            <div 
              style={{ 
                padding: '12px', 
                border: '1px solid #d9d9d9', 
                borderRadius: '6px', 
                backgroundColor: '#fafafa',
                minHeight: '400px',
                height: '400px',
                overflow: 'auto',
                cursor: 'pointer'
              }}
              onClick={() => setIsEditing(true)}
            >
              <div className="markdown-content">
                <Markdown content={form.getFieldValue('content') || template.getContent()} />
              </div>
            </div>
          )}
        </Form.Item>

        <Form.Item
          name="name"
          label={<Text type="secondary">{t('note_templates.name')}</Text>}
        >
          <Input />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={handleCancel}>
              {t('common.cancel')}
            </Button>
            <Button 
              type="primary" 
              htmlType="submit"
              loading={isSaving}
            >
              {t('common.save')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default NoteTemplateUpdateModal;
